import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { TenantRoleService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form, Input } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAdminNavigate from "@/hooks/useAdminNavigate";

const RoleFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useAdminNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use React Query for mutations
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    if (isEditMode) {
      // Check for changes before updating
      if (!checkFormChanges(values, role ?? {}, notify)) {
        setIsSubmitting(false);
        return;
      }

      const response = await TenantRoleService.updateRole(id ?? "", values);
      if (response) {
        navigate(navigationRoutes.ROLES);
      }
    } else {
      const response = await TenantRoleService.createRole(values);
      if (response) {
        navigate(navigationRoutes.ROLES);
      }
    }
    setIsSubmitting(false);
  };

  // Use React Query for data fetching
  const { data: role, isLoading } = useQuery({
    queryKey: ["role", id],
    queryFn: () => TenantRoleService.getRoleById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && role) {
      form.setFieldsValue(role);
    }
  }, [isEditMode, role, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !role) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Role" : "Add Role"}
          onBack={() => navigate(navigationRoutes.ROLES)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={isEditMode ? "Save Role Changes" : "Save New Role"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="max-w-xl"
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter role name" }]}
        >
          <Input placeholder="Enter role name" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea
            style={{ resize: "none" }}
            placeholder="Enter description"
            rows={3}
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default RoleFormPage;
